import React from 'react';

// Supermove
import {Space} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useResponsive} from '@supermove/hooks';
import {CallrailIntegrationModel, OrganizationModel} from '@supermove/models';

// App
import DrawerWithAction from '@shared/design/components/Drawer/DrawerWithAction';
import MultiEntryFieldInput from '@shared/design/components/Field/MultiEntryFieldInput';
import UpdateCallrailIntegrationForm from '@shared/modules/CallrailIntegration/forms/UpdateCallrailIntegrationForm';
import useUpdateCallrailIntegrationMutation from '@shared/modules/CallrailIntegration/hooks/useUpdateCallrailIntegrationMutation';
import JobTypeDropdown from '@shared/modules/Project/components/JobTypeDropdown';
import ProjectTypeDropdown from '@shared/modules/Project/components/ProjectTypeDropdown';

interface UpdateCallrailIntegrationDrawerProps {
  callrailIntegration: CallrailIntegrationModel;
  organization: OrganizationModel;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const UpdateCallrailIntegrationDrawer = ({
  callrailIntegration,
  organization,
  isOpen,
  onClose,
  onSuccess,
}: UpdateCallrailIntegrationDrawerProps) => {
  const responsive = useResponsive();

  const {form, submitting, handleSubmit} = useUpdateCallrailIntegrationMutation({
    updateCallrailIntegrationForm: UpdateCallrailIntegrationForm.new({
      callrailIntegration,
    }),
    onSuccess: () => {
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (errors) => {
      // Errors will be displayed on the form fields automatically
      console.log('Form validation errors:', errors);
    },
  });

  return (
    <DrawerWithAction
      isOpen={isOpen}
      handleClose={onClose}
      headerText='Update CallRail Integration'
      description='Update the settings for your CallRail integration'
      isDisabled={submitting}
      isSubmitting={submitting}
      handleSubmit={handleSubmit}
      primaryActionText='Save Changes'
      secondaryActionText='Cancel'
    >
      <ProjectTypeDropdown
        organization={organization}
        form={form}
        field='updateCallrailIntegrationForm'
        jobFormField='updateCallrailIntegrationForm.jobTypeId'
      />
      <Space height={16} />
      <JobTypeDropdown
        organization={organization}
        form={form}
        field='updateCallrailIntegrationForm'
        projectTypeId={form.values.updateCallrailIntegrationForm?.projectTypeId}
      />
      <Space height={24} />
      <MultiEntryFieldInput
        form={form}
        field='updateCallrailIntegrationForm.callrailExcludedPhoneNumbers'
        label='Skip Phone Numbers'
        placeholder='Enter phone number (10 digits)'
        tooltip='Phone numbers entered here will be excluded from call tracking'
      />
    </DrawerWithAction>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
UpdateCallrailIntegrationDrawer.fragment = gql`
  ${ProjectTypeDropdown.fragment}
  ${JobTypeDropdown.fragment}

  fragment UpdateCallrailIntegrationDrawer on CallrailIntegration {
    id
    projectTypeId
    jobTypeId
    callrailExcludedPhoneNumbers {
      id
      number
    }
    organization {
      id
      ...ProjectTypeDropdown
      ...JobTypeDropdown
    }
  }
`;

export default UpdateCallrailIntegrationDrawer;
